import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Alert, Button, Group, Loader, Menu, Modal, PasswordInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { formatDate, formatTime, PASSWORD_MIN, ROLES, type RoleId, type UserRow, type UsersResponse } from '@parts/shared';
import { errorText, getJson, postJson, putJson, requestDelete } from '../../api';
import { loadMe, useAuth } from '../../auth';
import { PageHeader } from '../../components/layout/PageHeader';
import { Avatar, StatusPill } from '../../components/ui';
import { useI18n } from '../../i18n';
import { IconCheck, IconDots, IconUser } from '../../icons';
import classes from './UsersScreen.module.css';

type Answer = { ok: boolean; message: string };

const STATUS_TITLE: Record<string, string> = { active: 'Работает', pending: 'Ждёт подтверждения', disabled: 'Отключён' };
const STATUS_TONE = { active: 'green', pending: 'amber', disabled: 'gray' } as const;

/**
 * Пользователи (только администратор): подтверждение заявок, роли, отключение, сброс пароля, добавление.
 * Роль определяет, что человек может проводить; имя — то, что видно в истории.
 */
export function UsersScreen() {
  const { t, tx } = useI18n();
  const auth = useAuth();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [passwordFor, setPasswordFor] = useState<UserRow | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers((await getJson<UsersResponse>('/api/users')).users);
      setError(null);
      void loadMe();
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (p: Promise<Answer>) => {
    try {
      const res = await p;
      notifications.show({ color: res.ok ? 'green' : 'red', message: tx(res.message) });
      await load();
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    }
  };

  const update = (u: UserRow, patch: Partial<{ role: RoleId; status: string; name: string }>) =>
    act(putJson<Answer>(`/api/users/${u.id}`, patch));

  const remove = (u: UserRow) =>
    modals.openConfirmModal({
      title: t('Удалить пользователя {name}?', { name: u.name }),
      children: <Text size="sm">{t('Войти он больше не сможет. Его действия в истории сохранятся.')}</Text>,
      labels: { confirm: t('Удалить'), cancel: t('Отмена') },
      confirmProps: { color: 'red' },
      onConfirm: () => void act(requestDelete<Answer>(`/api/users/${u.id}`))
    });

  const roles = ROLES.map((r) => ({ value: r.id, label: tx(r.title) }));
  const pending = (users ?? []).filter((u) => u.status === 'pending');
  const rest = (users ?? []).filter((u) => u.status !== 'pending');

  const row = (u: UserRow) => (
    <Table.Tr key={u.id} data-status={u.status}>
      <Table.Td>
        <Group gap={10} wrap="nowrap">
          <Avatar name={u.name} size={28} />
          <div className={classes.who}>
            <div className={classes.name}>
              {u.name}
              {u.id === auth.user?.id && <span className={classes.me}>{t('это вы')}</span>}
            </div>
            <div className={classes.login}>{u.login}</div>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Select
          value={u.role}
          data={roles}
          size="xs"
          w={170}
          allowDeselect={false}
          onChange={(v) => v && v !== u.role && void update(u, { role: v as RoleId })}
        />
      </Table.Td>
      <Table.Td>
        <StatusPill status={t(STATUS_TITLE[u.status] ?? u.status)} tone={STATUS_TONE[u.status]} />
      </Table.Td>
      <Table.Td className={classes.dim}>{u.lastLoginAt ? `${formatDate(u.lastLoginAt)}, ${formatTime(u.lastLoginAt)}` : '—'}</Table.Td>
      <Table.Td>
        <Group gap={6} justify="flex-end" wrap="nowrap">
          {u.status === 'pending' && (
            <Button size="xs" color="go" leftSection={<IconCheck size={14} />} onClick={() => void update(u, { status: 'active' })}>
              {t('Подтвердить')}
            </Button>
          )}
          <Menu position="bottom-end" width={220}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" aria-label={t('Действия')}>
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => setPasswordFor(u)}>{t('Задать новый пароль')}</Menu.Item>
              {u.status === 'active' ? (
                <Menu.Item onClick={() => void update(u, { status: 'disabled' })} disabled={u.id === auth.user?.id}>
                  {t('Отключить')}
                </Menu.Item>
              ) : (
                <Menu.Item onClick={() => void update(u, { status: 'active' })}>{t('Включить')}</Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item color="red" onClick={() => remove(u)} disabled={u.id === auth.user?.id}>
                {t('Удалить')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  );

  const table = (list: UserRow[]) => (
    <div className={classes.tableWrap}>
      <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover layout="fixed" miw={760} className={classes.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="34%">{t('Пользователь')}</Table.Th>
            <Table.Th w="20%">{t('Роль')}</Table.Th>
            <Table.Th w="16%">{t('Статус')}</Table.Th>
            <Table.Th w="16%">{t('Последний вход')}</Table.Th>
            <Table.Th w="14%" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{list.map(row)}</Table.Tbody>
      </Table>
    </div>
  );

  return (
    <div className={classes.page}>
      <PageHeader
        title={t('Пользователи')}
        count={users?.length}
        subtitle={t('доступ, роли и заявки на вход')}
        actions={
          <Button leftSection={<IconUser size={15} />} onClick={() => setAdding(true)}>
            {t('Добавить пользователя')}
          </Button>
        }
      />
      <div className={classes.body}>
        {error ? (
          <Alert color="red" variant="light">
            {tx(error)}
          </Alert>
        ) : !users ? (
          <Group gap={10} c="dimmed">
            <Loader size="sm" />
            {t('Загружаю данные…')}
          </Group>
        ) : (
          <Stack gap="lg">
            {pending.length > 0 && (
              <section>
                <h2 className={classes.sectionTitle}>
                  {t('Ждут подтверждения')} <span className={classes.badge}>{pending.length}</span>
                </h2>
                <Text size="sm" c="dimmed" mb={10}>
                  {t('Выберите роль и нажмите «Подтвердить» — после этого человек сможет войти.')}
                </Text>
                {table(pending)}
              </section>
            )}
            <section>
              {pending.length > 0 && <h2 className={classes.sectionTitle}>{t('Пользователи')}</h2>}
              {table(rest)}
            </section>
            <Text size="xs" c="dimmed">
              {t('Роли: «Продажи» — запросы клиента и перенос остатка; «Закупки» — наличие и закупка; «Администратор» — всё, включая пользователей и отмену действий.')}
            </Text>
          </Stack>
        )}
      </div>

      <AddUserModal opened={adding} onClose={() => setAdding(false)} onDone={() => void load()} />
      <PasswordModal user={passwordFor} onClose={() => setPasswordFor(null)} />
    </div>
  );
}

function AddUserModal({ opened, onClose, onDone }: { opened: boolean; onClose: () => void; onDone: () => void }) {
  const { t, tx } = useI18n();
  const [login, setLogin] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<RoleId>('sales');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (opened) {
      setLogin('');
      setName('');
      setRole('sales');
      setPassword('');
    }
  }, [opened]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await postJson<Answer>('/api/users', { login, name, role, password });
      notifications.show({ color: res.ok ? 'green' : 'red', message: tx(res.message) });
      if (res.ok) {
        onClose();
        onDone();
      }
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('Добавить пользователя')}>
      <Stack gap="md">
        <TextInput label={t('E-mail')} value={login} onChange={(e) => setLogin(e.target.value)} required />
        <TextInput label={t('Имя')} description={t('Оно будет видно в истории действий.')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Select
          label={t('Роль')}
          value={role}
          onChange={(v) => v && setRole(v as RoleId)}
          data={ROLES.map((r) => ({ value: r.id, label: tx(r.title) }))}
          allowDeselect={false}
        />
        <PasswordInput
          label={t('Пароль')}
          description={t('Не короче {n} символов. Сообщите его пользователю — сменить можно в меню «Имя и пароль».', { n: PASSWORD_MIN })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('Отмена')}
          </Button>
          <Button onClick={() => void save()} loading={busy}>
            {t('Добавить')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PasswordModal({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { t, tx } = useI18n();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => setPassword(''), [user]);

  const save = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const res = await postJson<Answer>(`/api/users/${user.id}/password`, { password });
      notifications.show({ color: res.ok ? 'green' : 'red', message: tx(res.message) });
      if (res.ok) onClose();
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={!!user} onClose={onClose} title={t('Новый пароль: {name}', { name: user?.name ?? '' })}>
      <Stack gap="md">
        <PasswordInput
          label={t('Новый пароль')}
          description={t('Не короче {n} символов. Пользователь выйдет со всех устройств.', { n: PASSWORD_MIN })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('Отмена')}
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={password.length < PASSWORD_MIN}>
            {t('Сохранить')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
